import type { MouseEvent } from 'react';
import { Box, Tooltip, makeStyles } from '@material-ui/core';
import clsx from 'clsx';
import { WarningTriangleIcon } from '../sicons';

const useStyles = makeStyles(() => ({
  // Overhangs the top-right corner of the price input (same anchor as the
  // field-lock avatar) so it never covers the value and can't push the input
  // off-centre in rows that have no warning.
  badge: {
    position: 'absolute',
    top: -8,
    right: -8,
    zIndex: 5,
    display: 'inline-flex',
    padding: 2,
    border: 0,
    borderRadius: '50%',
    backgroundColor: '#FFF2E3',
    boxShadow: '0 0 0 1px #FFD9AB inset',
    cursor: 'default'
  },
  // Inline variant for read-only price cells (catalog, PDF-less tables) that
  // have no input to hang a badge off.
  inlineBadge: {
    position: 'static',
    padding: 0,
    backgroundColor: 'transparent',
    boxShadow: 'none'
  },
  clickable: {
    cursor: 'pointer'
  },
  // Wider than MUI's default so a reason wraps over two comfortable lines
  // instead of five narrow ones. Background/radius come from the app's global
  // MuiTooltip override — only layout is set here.
  surface: {
    maxWidth: 240,
    padding: '10px 12px',
    textAlign: 'center',
    lineHeight: 1.45
  },
  tooltip: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 2
  },
  headline: {
    fontWeight: 600
  },
  // Muted against the headline, so the two lines read as statement + detail
  // rather than one run-on sentence.
  reason: {
    color: 'rgba(255, 255, 255, 0.72)'
  },
  // A chip rather than a bare link: inside a tooltip the target has to be easy
  // to hit on the way in from the badge.
  retry: {
    marginTop: 6,
    padding: '4px 12px',
    background: 'rgba(255, 255, 255, 0.1)',
    border: 0,
    borderRadius: 99,
    color: '#7FC4FD',
    font: 'inherit',
    fontWeight: 600,
    cursor: 'pointer',
    '&:hover': {
      background: 'rgba(255, 255, 255, 0.18)'
    },
    '&:disabled': {
      background: 'rgba(255, 255, 255, 0.06)',
      color: '#AFBAC6',
      cursor: 'default'
    }
  },
  note: {
    marginTop: 4,
    color: 'rgba(255, 255, 255, 0.6)'
  }
}));

/**
 * Server reasons arrive in whatever shape the pricing service sent them ("No
 * sell price returned", "no new price was returned"). Normalise so the detail
 * line always reads as one sentence.
 */
const formatReason = (reason: string) => {
  const trimmed = reason.trim();
  const sentence = trimmed.charAt(0).toUpperCase() + trimmed.slice(1);
  return /[.!?]$/.test(sentence) ? sentence : `${sentence}.`;
};

interface PricingFailedIndicatorProps {
  /**
   * Server-side pricing failed for this item (`pricingStatus === 'failed'`), or
   * — on the client-priced surfaces (Quotes V1, Catalog) — the pricing call
   * returned no usable price for it. Nothing renders when false and no
   * `reason` is given.
   */
  failed?: boolean;
  /**
   * Why it failed, when known (the server's `failed[].reason`). Shown in the
   * tooltip; on its own it's also enough to render the indicator.
   */
  reason?: string | null;
  /** Wires up the "Try again" action. Omit to render an explanation only. */
  onRetry?: () => void;
  isRetrying?: boolean;
  /** Blocks "Try again" (e.g. the quote isn't editable) and says why. */
  retryDisabled?: boolean;
  retryDisabledTooltip?: string;
  /** Render in the flow of a read-only cell instead of over a price input. */
  inline?: boolean;
}

/**
 * "Live pricing isn't available" indicator (DEV-495).
 *
 * Flags an item whose price lookup produced nothing usable — the sell/cost
 * shown are the previous ones (or none at all) — and offers a retry. Keyed on
 * an explicit failure flag, never on `sell === 0`, so a legitimately free item
 * is not flagged.
 *
 * When anchoring over a price input, render it inside a `position: relative`
 * wrapper. For read-only cells pass `inline`.
 */
export const PricingFailedIndicator = ({
  failed,
  reason,
  onRetry,
  isRetrying,
  retryDisabled,
  retryDisabledTooltip,
  inline
}: PricingFailedIndicatorProps) => {
  const classes = useStyles();

  if (!failed && !reason) return null;

  const title = (
    <Box className={classes.tooltip}>
      <span className={classes.headline}>
        Live pricing isn&apos;t available
      </span>
      <span className={classes.reason}>
        {/* No usable reason from the server — ask for the one thing the rep can
            actually do. Not "the price shown isn't from pricing", which only
            said what the number wasn't, and not "showing its last saved price",
            which there may not be. Wording tracks the quote-level banner
            (PricingFailedBanner) so a row and the banner above it agree. */}
        {reason?.trim()
          ? formatReason(reason)
          : 'Please confirm this price before proceeding.'}
      </span>
      {onRetry && (
        <button
          type="button"
          className={classes.retry}
          disabled={isRetrying || retryDisabled}
          onClick={event => {
            event.stopPropagation();
            onRetry();
          }}>
          {isRetrying ? 'Trying again…' : 'Try again'}
        </button>
      )}
      {onRetry && retryDisabled && retryDisabledTooltip && (
        <span className={classes.note}>{retryDisabledTooltip}</span>
      )}
    </Box>
  );

  // Only a badge that can actually be clicked becomes a button, and it is never
  // given the `disabled` attribute: a disabled element fires no mouse events, so
  // MUI could not open this tooltip from it (it warns about exactly that) — the
  // "Live pricing isn't available" explanation would go missing on the rows
  // that can't be re-priced, which are the ones that most need it. Retrying is
  // guarded in the handler instead, and the "Try again" button inside the
  // tooltip still greys itself out.
  const isInteractive = !!onRetry && !retryDisabled;

  return (
    // `interactive` keeps the tooltip open while the pointer travels into it,
    // so "Try again" is actually clickable.
    <Tooltip
      arrow
      interactive
      title={title}
      classes={{ tooltip: classes.surface }}>
      <Box
        component={isInteractive ? 'button' : 'div'}
        {...(isInteractive
          ? {
              type: 'button',
              onClick: (event: MouseEvent) => {
                event.stopPropagation();
                if (!isRetrying) onRetry();
              }
            }
          : {})}
        className={clsx(
          classes.badge,
          inline && classes.inlineBadge,
          isInteractive && classes.clickable
        )}>
        <WarningTriangleIcon
          htmlColor="#FF9F29"
          style={{ width: 15, height: 14 }}
        />
      </Box>
    </Tooltip>
  );
};
